/* eslint-disable max-lines */
import { FormattedMessage, useIntl } from 'react-intl';

import type { ScanProgress } from '@/hooks/use-scan-progress';
import type { TranslationKeys } from '@/lib/translations/types';
import { cn } from '@/lib/utils';
import type { FileRouteTypes } from '@/routeTree.gen';

import {
  IntegrationButton,
  ScanningStatusText,
  WarningsTooltip,
} from './IntegrationHelpers';

interface ColorOptions {
  connected: boolean;
  available: boolean;
  pending: boolean;
  isScanning: boolean;
  isSyncing: boolean;
  donePercentage?: number;
  warnings?: TranslationKeys[];
}

const shouldShowStatusRibbon = (options: ColorOptions) => {
  const hasErrors = (options.warnings ?? []).length > 0;
  const hasErrorStatus = options.donePercentage === 0 && options.connected;

  return (
    options.connected ||
    hasErrors ||
    hasErrorStatus ||
    options.isScanning ||
    options.isSyncing
  );
};

const getRibbonColor = (options: ColorOptions) => {
  const hasErrors = (options.warnings ?? []).length > 0;
  const hasErrorStatus = options.donePercentage === 0 && options.connected;

  // Scanning and syncing states should be blue
  if (options.isScanning || options.isSyncing) {
    return 'bg-blue-600 dark:bg-blue-700 text-white';
  }

  // Error states should be red
  if (hasErrors || hasErrorStatus) {
    return 'bg-red-600 dark:bg-red-700 text-white';
  }

  // Connected state should be green
  if (options.connected) {
    return 'bg-green-600 dark:bg-green-700 text-white';
  }

  return '';
};

interface TextOptions {
  connected: boolean;
  available: boolean;
  pending: boolean;
  donePercentage?: number;
  isScanning: boolean;
  scanPercentage?: number;
  isSyncing: boolean;
}

const getStatusText = (options: TextOptions): string | null => {
  const { pending, isScanning, scanPercentage, isSyncing } = options;
  if (pending) {
    return '...';
  }
  if (isScanning && scanPercentage !== undefined) {
    return `Scanning... (${scanPercentage}%)`;
  }
  if (isSyncing) {
    return 'Syncing';
  }

  return null;
};

const getText = (
  options: TextOptions,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  const { connected, available, donePercentage } = options;

  const statusText = getStatusText(options);
  if (statusText !== null) {
    return statusText;
  }

  if (!available) {
    return 'Coming soon';
  }

  if (connected && donePercentage !== undefined && donePercentage < 100) {
    return `${formatMessage({ id: 'global.status.connected' })} (${donePercentage}%)`;
  }

  return connected
    ? formatMessage({ id: 'global.status.connected' })
    : formatMessage({ id: 'global.status.readyToConnect' });
};

interface Props {
  logoUrl: string;
  connected: boolean;
  title: TranslationKeys;
  description?: TranslationKeys;
  available: boolean;
  onClick?: () => void;
  to?: FileRouteTypes['to'];
  pending?: boolean;
  warnings?: TranslationKeys[];
  donePercentage?: number;
  scanProgress?: ScanProgress;
  isSyncing?: boolean;
}

// eslint-disable-next-line complexity
export const Integration = ({
  title,
  description,
  logoUrl,
  connected,
  available,
  to,
  onClick = () => undefined,
  pending = false,
  warnings = [],
  donePercentage,
  scanProgress,
  isSyncing = false,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const isScanning = scanProgress !== undefined;
  const scanPercentage = scanProgress
    ? Math.floor(
        (100 * scanProgress.servicesScannedCount) /
          Math.max(scanProgress.totalServicesToScan, 1),
      )
    : undefined;

  const colorOptions = {
    connected,
    available,
    pending,
    isScanning,
    isSyncing,
    donePercentage,
    warnings,
  };

  const showStatusRibbon = shouldShowStatusRibbon(colorOptions);

  return (
    <div className="border border-gray-400 p-1.5 flex flex-col items-center rounded-sm gap-1.5 relative min-h-44 justify-between">
      {/* Status Ribbon */}
      {showStatusRibbon && (
        <div
          className={cn(
            'absolute top-0 left-0 right-0 text-xs px-2 py-1 text-center rounded-t-sm',
            getRibbonColor(colorOptions),
          )}
        >
          {isScanning ? (
            <ScanningStatusText scanPercentage={scanPercentage ?? 0} />
          ) : (
            getText(
              {
                connected,
                available,
                pending,
                donePercentage,
                isScanning,
                scanPercentage,
                isSyncing,
              },
              formatMessage,
            )
          )}
          <WarningsTooltip warnings={warnings} connected={connected} />
        </div>
      )}

      {/* Top section: Logo */}
      <div className="flex flex-col items-center gap-1.5">
        <div
          className={cn(
            'dark:bg-gray-200 rounded-sm p-1',
            showStatusRibbon && 'mt-6',
          )}
        >
          <img className="w-[32px] h-[32px]" src={logoUrl} alt={title} />
        </div>
      </div>

      {/* Middle section: Description (flexible space) */}
      <div className="flex-1 flex items-center justify-center px-2 py-1">
        {description !== undefined && (
          <p className="text-xs text-gray-600 dark:text-gray-300 text-center leading-tight">
            <FormattedMessage id={description} />
          </p>
        )}
      </div>

      {/* Bottom section: Button (fixed position) */}
      <div className="w-full">
        <IntegrationButton
          to={to}
          available={available}
          onClick={onClick}
          title={title}
          formatMessage={formatMessage}
          connected={connected}
        />
      </div>
    </div>
  );
};
